/**
 * CRM › Onboarding (v7 CORE-2 §4): the work queue of customers who are starting, and the 6-step wizard that makes a
 * customer who WILL be delivered to — 1 Customer · 2 Door · 3 Route · 4 Plan · 5 Door QR · 6 Review.
 *
 * The wizard holds its answers until Review; then one call (api.onboard_customer) does it all in one transaction:
 * the code from the allocator, the door and pin, the route and place, the sticker, the plan, the readiness checks.
 * "Activate" activates when nothing blocks; "Save as draft" keeps the customer in Starting soon with its blockers.
 * A price that is not set is a blocker in plain words ("Price not set yet"); the desk never shows a price it invented.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, Crosshair, MapPin, Plus, Printer } from 'lucide-react';
import { Page } from '../../shell/AppShell';
import { rpc, supabase } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import { variantOptions } from '../../lib/variantOptions';
import { Button, EmptyState, ErrorState, LoadingRows, Panel, PanelHeader, cx, plainError } from '../../ui/primitives';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/forms';
import { TileMap, type MapView, type TileSlot } from '../delivery/TileMap';
import { toast } from '../../ui/toast';
import * as f from '../../ui/format';
import { MemberRows, type MemberDraft } from './household';
import { parsePin } from '../../lib/mapPin';
import { EVERY_OPTIONS, cycleFor, type Every } from '../../lib/itemDays';

interface Blocker { code: string; step: number; text: string; refusal?: string }
interface QueueRow { customerId: string; code: string; name: string; status: string; route: string | null; firstDelivery: string | null; blockers: Blocker[] }
interface Suggestion { routeId: string; route: string; name: string; metres: number; rider: string | null; hasRider: boolean; position: number; doors: number;
  before: { code: string; name: string } | null; after: { code: string; name: string } | null }
interface Variant { id: string; code: string; name: string; size_class: string | null }

const STEPS = ['Customer', 'Door', 'Route', 'Plan', 'Door QR', 'Review'] as const;
const WEEKDAYS = [['1', 'Mon'], ['2', 'Tue'], ['3', 'Wed'], ['4', 'Thu'], ['5', 'Fri'], ['6', 'Sat'], ['7', 'Sun']] as const;
const JODHPUR: MapView = { center: { lat: 26.2389, lng: 73.0243 }, zoom: 13 };

export function OnboardingScreen() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const open = params.get('new') === '1' || !!params.get('lead');
  const close = () => setParams((p) => { const n = new URLSearchParams(p); n.delete('new'); n.delete('lead'); return n; }, { replace: true });
  if (open && can('customer.create')) return <Wizard onDone={close} leadId={params.get('lead')} />;
  return <Queue onNew={() => setParams({ new: '1' })} mayCreate={can('customer.create')} />;
}

function Queue({ onNew, mayCreate }: { onNew: () => void; mayCreate: boolean }) {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['onboarding-queue'], queryFn: () => rpc<QueueRow[]>('onboarding_queue') });
  return (
    <Page title="Onboarding" subtitle="Everybody who is starting, and what still stands between them and their first delivery."
      actions={mayCreate ? <Button variant="primary" onClick={onNew}><Plus size={14} aria-hidden /> New customer</Button> : undefined}>
      <Panel>
        <PanelHeader title="Starting soon" note={q.data ? `${q.data.length} customer(s)` : undefined} />
        {q.error ? <ErrorState error={q.error} retry={() => void q.refetch()} /> : !q.data ? <LoadingRows rows={4} columns={4} />
          : q.data.length === 0 ? <EmptyState title="Nobody is starting" detail="A new customer appears here until their first delivery. Add one with New customer." />
          : (
          <ul className="divide-y divide-line">
            {q.data.map((r) => (
              <li key={r.customerId}>
                <button type="button" onClick={() => navigate(`/customers/${r.customerId}`)} className="flex w-full items-start justify-between gap-4 px-4 py-3 text-left hover:bg-surface-sunken">
                  <div className="min-w-0">
                    <div className="font-medium text-ink">{r.name} <span className="font-mono text-small text-ink-muted">{r.code}</span></div>
                    <div className="text-small text-ink-muted">{r.route ?? 'No route yet'}{r.firstDelivery ? ` · first delivery ${f.date(r.firstDelivery)}` : ''}</div>
                  </div>
                  <div className="text-right text-small">
                    {r.blockers.length === 0 ? <span className="text-accepted">Ready</span>
                      : r.blockers.map((b) => <div key={b.code + b.text} className="text-queued">{b.text}</div>)}
                  </div>
                </button>
              </li>))}
          </ul>)}
      </Panel>
    </Page>
  );
}

export interface Draft {
  name: string; phone: string; altPhone: string; whatsapp: boolean; language: string; channel: string; email: string;
  consentWhatsapp: boolean; consentEmail: boolean; source: string; referredBy: string;
  addressLine: string; area: string; landmark: string; pincode: string; instructions: string; lat: number | null; lng: number | null;
  routeId: string; position: number | null;
  pattern: 'DAILY' | 'ALTERNATE' | 'WEEKDAYS'; weekdays: string[]; trial: boolean; startDate: string; depositBottles: string;
  /** An item's own days (the owner, 2026-10-24); none given: the plan's "How often". */
  items: { variantId: string; quantity: number; every?: Every; weekdays?: string[] }[];
  labelMode: 'allot' | 'scan' | 'later'; labelPayload: string;
  selfDob: string; selfAnniversary: string; selfGender: string; members: MemberDraft[];
  /** More addresses kept on record (office, parents…); deliveries go to the first one (the owner, 2026-10-22). */
  moreDoors: DoorDraft[];
}

/** One address as the form holds it: the onboarding wizard and Customer 360's edit form share it. */
export interface DoorDraft {
  id?: string; label: string; addressLine: string; area: string; landmark: string; pincode: string; instructions: string; lat: number | null; lng: number | null;
}
export const EMPTY_DOOR: DoorDraft = { label: '', addressLine: '', area: '', landmark: '', pincode: '', instructions: '', lat: null, lng: null };
/** The add_customer_door shape of an address. */
export const doorPayload = (x: DoorDraft) => ({ label: x.label.trim() || null, addressLine: x.addressLine.trim(), area: x.area.trim() || null, landmark: x.landmark.trim() || null,
  pincode: x.pincode.trim() || null, instructions: x.instructions.trim() || null, lat: x.lat, lng: x.lng });
export const EMPTY: Draft = {
  name: '', phone: '', altPhone: '', whatsapp: true, language: 'hinglish', channel: 'WHATSAPP', email: '', consentWhatsapp: true, consentEmail: false, source: '', referredBy: '',
  addressLine: '', area: '', landmark: '', pincode: '', instructions: '', lat: null, lng: null, routeId: '', position: null,
  pattern: 'DAILY', weekdays: ['1', '2', '3', '4', '5', '6', '7'], items: [{ variantId: '', quantity: 1 }], trial: false, startDate: '', depositBottles: '',
  labelMode: 'allot', labelPayload: '',
  selfDob: '', selfAnniversary: '', selfGender: '', members: [], moreDoors: [],
};

function Wizard({ onDone, leadId }: { onDone: () => void; leadId?: string | null }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [d, setD] = useState<Draft>(EMPTY);
  const [key] = useState(() => crypto.randomUUID());
  // v7 CORE-7b: a lead converted here arrives filled from the lead, and becomes Won with the customer it makes.
  const lead = useQuery({ queryKey: ['lead', leadId], enabled: !!leadId, queryFn: () => rpc<{ prefill: { name: string; mobile: string | null; area: string | null; address: string | null; source: string } }>('lead', { p_lead_id: leadId }) });
  const [prefilled, setPrefilled] = useState(false);
  if (lead.data && !prefilled) {
    const p = lead.data.prefill;
    setPrefilled(true);
    setD((x) => ({ ...x, name: p.name, phone: (p.mobile ?? '').replace(/^\+91/, ''), area: p.area ?? '', addressLine: p.address ?? '', source: p.source }));
  }
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
  const earliest = useQuery({ queryKey: ['earliest-start'], queryFn: () => rpc<{ date: string; words: string }>('earliest_start') });
  const start = d.startDate || earliest.data?.date || '';

  const payload = () => ({
    reason: 'New customer onboarded at the desk',
    customer: { name: d.name, phone: d.phone, altPhone: d.altPhone || null, whatsapp: d.whatsapp, language: d.language, channel: d.channel, email: d.email || null,
                consentWhatsapp: d.consentWhatsapp, consentEmail: d.consentEmail, source: d.source || null, referredBy: d.referredBy || null },
    door: { addressLine: d.addressLine, area: d.area, landmark: d.landmark, pincode: d.pincode, instructions: d.instructions, lat: d.lat, lng: d.lng },
    ...(d.routeId ? { route: { routeId: d.routeId, position: d.position } } : {}),
    plan: { ...planOf(d, start), trial: d.trial, startDate: start, depositBottles: d.depositBottles ? Number(d.depositBottles) : null },
    label: d.labelMode === 'later' ? { skip: true } : d.labelMode === 'scan' ? { payload: d.labelPayload } : {},
    // The household: the customer themself (when a date is given) and each family member with a name.
    members: [
      ...(d.selfDob || d.selfAnniversary || d.selfGender ? [{ name: d.name, relation: 'self', dateOfBirth: d.selfDob || null, anniversary: d.selfAnniversary || null, gender: d.selfGender || null, isAlive: true, mayReceive: true }] : []),
      ...d.members.filter((m) => m.name.trim() && m.relation).map((m) => ({ ...m, dateOfBirth: m.dateOfBirth || null, anniversary: m.anniversary || null, phone: m.phone || null })),
    ],
  });
  const save = useMutation({
    mutationFn: (activate: boolean) => rpc<{ customerId: string; code: string; status: string; detail: string; blockers: Blocker[] }>('onboard_customer',
      { p_payload: payload(), p_idempotency_key: key, p_activate: activate }),
    onSuccess: async (r) => {
      void qc.invalidateQueries({ queryKey: ['onboarding-queue'] }); void qc.invalidateQueries({ queryKey: ['customers-list'] });
      toast(r.detail, { tone: r.status === 'ACTIVATED' ? 'ok' : 'info', ms: 8000 });
      // The other addresses, after the customer exists: each is kept on record; deliveries stay at the first.
      for (const x of d.moreDoors.filter((m) => m.addressLine.trim().length >= 5)) {
        try { await rpc('add_customer_door', { p_customer_id: r.customerId, p_door: doorPayload(x), p_reason: 'Another address given at onboarding', p_idempotency_key: crypto.randomUUID() }); }
        catch (e) { toast(`Address "${x.addressLine.trim()}" was not saved: ${plainError((e as Error).message)}. Add it on the customer's page.`, { tone: 'bad', ms: 12000 }); }
      }
      if (leadId) void rpc('lead_action', { p_lead_id: leadId, p_action: 'CONVERTED', p_body: null, p_stage: null, p_follow_up_at: null, p_customer_id: r.customerId });
      onDone(); navigate(`/customers/${r.customerId}`);
    },
  });

  const stepOk = [
    d.name.trim().length >= 2 && /^\D*([6-9]\d{9})\D*$/.test(d.phone.replace(/^(\+?91|0)/, '').replace(/\s/g, '')) ,
    d.addressLine.trim().length >= 5 && d.moreDoors.every((x) => x.addressLine.trim().length >= 5),
    true, d.items.some((i) => i.variantId && i.quantity > 0) && d.items.filter((i) => i.variantId).every((i) => itemDays(d, i).every !== 'WEEKDAYS' || itemDays(d, i).weekdays.length > 0),
    d.labelMode !== 'scan' || d.labelPayload.trim().length > 6, true,
  ];

  return (
    <Page title="New customer" subtitle="Six steps; nothing is saved until Review. A customer can be started with a pin or a sticker still to come."
      actions={<Button onClick={onDone}>Back to the queue</Button>}>
      <ol className="mb-4 flex flex-wrap gap-2" aria-label="Steps">
        {STEPS.map((s, i) => (
          <li key={s}>
            <button type="button" onClick={() => setStep(i)} aria-current={i === step ? 'step' : undefined}
              className={cx('rounded-full border px-3 py-1 text-small', i === step ? 'border-brand bg-brand/10 text-ink' : 'border-line text-ink-muted')}>
              {i + 1}. {s}
            </button>
          </li>))}
      </ol>
      <Panel>
        <div className="grid gap-4 p-4">
          {step === 0 ? <StepCustomer d={d} set={set} /> : null}
          {step === 1 ? <StepDoor d={d} set={set} /> : null}
          {step === 2 ? <StepRoute d={d} set={set} /> : null}
          {step === 3 ? <StepPlan d={d} set={set} earliest={earliest.data} /> : null}
          {step === 4 ? <StepLabel d={d} set={set} /> : null}
          {step === 5 ? <StepReview d={d} start={start} /> : null}
          {save.error ? <p role="alert" className="text-small text-refused">{plainError((save.error as Error).message)}</p> : null}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-line px-4 py-3">
          <Button disabled={step === 0} onClick={() => setStep((s) => s - 1)}><ArrowLeft size={14} aria-hidden /> Back</Button>
          {step < 5
            ? <Button variant="primary" disabled={!stepOk[step]} onClick={() => setStep((s) => s + 1)}>Next <ArrowRight size={14} aria-hidden /></Button>
            : <div className="flex gap-2">
                <Button disabled={save.isPending || !stepOk.slice(0, 5).every(Boolean)} onClick={() => save.mutate(false)}>Save as draft</Button>
                <Button variant="primary" disabled={save.isPending || !stepOk.slice(0, 5).every(Boolean)} onClick={() => save.mutate(true)}><Check size={14} aria-hidden /> Activate</Button>
              </div>}
        </div>
      </Panel>
    </Page>
  );
}

export type StepProps = { d: Draft; set: <K extends keyof Draft>(k: K, v: Draft[K]) => void };

export function StepCustomer({ d, set }: StepProps) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Field label="Name" required><Input value={d.name} onChange={(e) => set('name', e.target.value)} autoFocus /></Field>
      <Field label="Mobile" required hint="10 digits"><Input value={d.phone} onChange={(e) => set('phone', e.target.value)} inputMode="tel" /></Field>
      <Field label="Second number"><Input value={d.altPhone} onChange={(e) => set('altPhone', e.target.value)} inputMode="tel" /></Field>
      <Field label="E-mail"><Input value={d.email} onChange={(e) => set('email', e.target.value)} type="email" /></Field>
      <Field label="Language"><Select value={d.language} onChange={(e) => set('language', e.target.value)}
        options={[{ value: 'hinglish', label: 'Hinglish' }, { value: 'hi', label: 'Hindi' }, { value: 'en', label: 'English' }]} /></Field>
      <Field label="Reach them by"><Select value={d.channel} onChange={(e) => set('channel', e.target.value)}
        options={[{ value: 'WHATSAPP', label: 'WhatsApp' }, { value: 'CALL', label: 'Call' }, { value: 'SMS', label: 'SMS' }, { value: 'EMAIL', label: 'E-mail' }]} /></Field>
      <Field label="Where they heard of us"><Input value={d.source} onChange={(e) => set('source', e.target.value)} placeholder="Neighbour, leaflet, walk-in…" /></Field>
      <Field label="Referred by" hint="A customer's code, if any"><Input value={d.referredBy} onChange={(e) => set('referredBy', e.target.value.toUpperCase())} placeholder="DSG-JDH-…" /></Field>
      <div className="grid gap-2 md:col-span-2">
        <Checkbox checked={d.whatsapp} onChange={(e) => set('whatsapp', e.target.checked)} label="This mobile has WhatsApp" />
        <Checkbox checked={d.consentWhatsapp} onChange={(e) => set('consentWhatsapp', e.target.checked)} label="Agrees to WhatsApp messages" />
        <Checkbox checked={d.consentEmail} onChange={(e) => set('consentEmail', e.target.checked)} label="Agrees to e-mails" />
      </div>
      <div className="grid gap-2 border-t border-line pt-3 md:col-span-2">
        <div className="text-section font-semibold">Family</div>
        <p className="text-small text-ink-muted">Birthdays and anniversaries of the household reach CRM › Overview, so a small gift can go on the day.</p>
        <MemberRows selfDob={d.selfDob} selfAnniversary={d.selfAnniversary} selfGender={d.selfGender}
          onSelf={(x) => { if (x.dob !== undefined) set('selfDob', x.dob); if (x.anniversary !== undefined) set('selfAnniversary', x.anniversary); if (x.gender !== undefined) set('selfGender', x.gender); }}
          rows={d.members} onRows={(rows) => set('members', rows)} />
      </div>
    </div>
  );
}

function StepDoor({ d, set }: StepProps) {
  const main: DoorDraft = { label: '', addressLine: d.addressLine, area: d.area, landmark: d.landmark, pincode: d.pincode, instructions: d.instructions, lat: d.lat, lng: d.lng };
  const setMain = (p: Partial<DoorDraft>) => { for (const [k, v] of Object.entries(p)) if (k !== 'label' && k !== 'id') set(k as keyof Draft, v as never); };
  return (
    <div className="grid gap-4">
      <div className="text-section font-semibold">Delivery address</div>
      <DoorFields v={main} set={setMain} />
      <MoreDoors rows={d.moreDoors} onRows={(rows) => set('moreDoors', rows)} />
    </div>
  );
}

/** More addresses, each in its own box, with Add another address. Deliveries do not go to them. */
export function MoreDoors({ rows, onRows, first = 2, extra }: { rows: DoorDraft[]; onRows: (rows: DoorDraft[]) => void; first?: number;
  /** Something more under an address (Customer 360's edit form: the address's own round). */ extra?: (x: DoorDraft) => ReactNode }) {
  return (
    <div className="grid gap-3 border-t border-line pt-3">
      <div>
        <div className="text-section font-semibold">Other addresses</div>
        <p className="text-small text-ink-muted">Kept on record: office, parents' house… Deliveries stay at the delivery address; to deliver somewhere else from a date, use Add / move address on the customer's page.</p>
      </div>
      {rows.map((x, i) => (
        <div key={i} className="rounded-md border border-line p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-small font-medium text-ink-muted">Address {i + first}</span>
            {/* An address already saved stays (its history is kept); only a new one can be taken out here. */}
            {x.id ? <span className="text-small text-ink-faint">saved</span> : <Button size="dense" onClick={() => onRows(rows.filter((_, j) => j !== i))}>Remove</Button>}
          </div>
          <DoorFields v={x} set={(p) => onRows(rows.map((r, j) => (j === i ? { ...r, ...p } : r)))} withLabel />
          {extra ? extra(x) : null}
        </div>))}
      <div><Button size="dense" onClick={() => onRows([...rows, { ...EMPTY_DOOR }])}><Plus size={14} aria-hidden /> Add another address</Button></div>
    </div>
  );
}

/** One address: the fields a rider reads, and the map to pin the door. */
export function DoorFields({ v, set, withLabel = false }: { v: DoorDraft; set: (p: Partial<DoorDraft>) => void; withLabel?: boolean }) {
  const web = useQuery({ queryKey: ['delivery-web-map'], queryFn: () => rpc<{ tiles?: TileSlot[] }>('delivery_web_map', {}) });
  const [view, setView] = useState<MapView>(v.lat !== null && v.lng !== null ? { center: { lat: v.lat, lng: v.lng }, zoom: 17 } : JODHPUR);
  // The spot can also be PASTED under the map (the owner, 2026-10-26): "28.4, 77.1" or a Google Maps link; the map moves there and the cross sits on it, and the person presses Pin the door here.
  const [pasted, setPasted] = useState('');
  const [pasteNote, setPasteNote] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const go = (lat: number, lng: number, swapped = false) => {
    setView({ center: { lat, lng }, zoom: 17 });
    // the owner, 2026-10-27: numbers or a link that someone typed are the pin; they are set at once (before, the pin was set only by Pin the door here and a typed spot was lost at Review)
    set({ lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) });
    setPasteNote({ tone: 'ok', text: `Pinned at ${lat.toFixed(6)}, ${lng.toFixed(6)}${swapped ? ' (the numbers were the other way round; fixed)' : ''}. Check the cross is on the door; to correct it, move the map and press Pin the door here.` });
  };
  const open = useMutation({
    mutationFn: async (link: string) => {
      const { data, error } = await supabase.functions.invoke('resolve-map-link', { method: 'POST', body: { url: link } });
      const r = (data ?? null) as { ok?: boolean; lat?: number; lng?: number; error?: string } | null;
      if (!r?.ok || typeof r.lat !== 'number' || typeof r.lng !== 'number') throw new Error(r?.error ?? error?.message ?? 'that link could not be opened');
      return { lat: r.lat, lng: r.lng };
    },
    onSuccess: (r) => go(r.lat, r.lng),
    onError: (e) => setPasteNote({ tone: 'bad', text: (e as Error).message }),
  });
  const applyPasted = (text: string) => {
    const r = parsePin(text);
    if (r.ok) { go(r.lat, r.lng, r.swapped); return; }
    if (r.short) { setPasteNote({ tone: 'ok', text: 'Opening the Google link…' }); open.mutate(r.short); return; }
    setPasteNote({ tone: 'bad', text: r.why });
  };
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
      <div className="grid content-start gap-3">
        {withLabel ? <Field label="Which address"><Input value={v.label} onChange={(e) => set({ label: e.target.value })} placeholder="Home, Office, Parents…" /></Field> : null}
        <Field label="Address" required hint="House, street — what a rider reads"><Input value={v.addressLine} onChange={(e) => set({ addressLine: e.target.value })} /></Field>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Area"><Input value={v.area} onChange={(e) => set({ area: e.target.value })} /></Field>
          <Field label="PIN code"><Input value={v.pincode} onChange={(e) => set({ pincode: e.target.value })} inputMode="numeric" /></Field>
        </div>
        <Field label="Landmark"><Input value={v.landmark} onChange={(e) => set({ landmark: e.target.value })} /></Field>
        <Field label="Instructions for the rider"><Textarea rows={2} value={v.instructions} onChange={(e) => set({ instructions: e.target.value })} placeholder="Ring once; leave at the gate" /></Field>
        <p className="text-small text-ink-muted">The gate photo is taken by the rider on the first delivery, or added on the customer's page.</p>
      </div>
      <div className="grid content-start gap-2">
        <div className="relative">
          <TileMap slots={web.data?.tiles ?? []} view={view} onView={setView} height={320}>
            {(project) => {
              const c = project(view.center);
              const pin = v.lat !== null && v.lng !== null ? project({ lat: v.lat, lng: v.lng }) : null;
              return <>
                <line x1={c.x - 10} y1={c.y} x2={c.x + 10} y2={c.y} stroke="currentColor" className="text-ink" />
                <line x1={c.x} y1={c.y - 10} x2={c.x} y2={c.y + 10} stroke="currentColor" className="text-ink" />
                {pin ? <circle cx={pin.x} cy={pin.y} r={7} className="fill-brand stroke-surface" strokeWidth={2} /> : null}
              </>;
            }}
          </TileMap>
        </div>
        <div className="flex items-center justify-between gap-2 text-small">
          <span className="text-ink-muted">{v.lat !== null && v.lng !== null ? <><MapPin size={12} className="inline" aria-hidden /> {v.lat.toFixed(6)}, {v.lng.toFixed(6)}</> : 'Move the map until the cross is on the door.'}</span>
          <Button size="dense" onClick={() => set({ lat: Number(view.center.lat.toFixed(6)), lng: Number(view.center.lng.toFixed(6)) })}>
            <Crosshair size={14} aria-hidden /> Pin the door here
          </Button>
        </div>
        <div className="grid gap-1">
          <label className="grid gap-0.5 text-small">Or paste the spot: numbers like 26.2389, 73.0243, or a Google Maps link
            <span className="flex items-center gap-1">
              <Input value={pasted} aria-label="Paste coordinates or a Google Maps link" placeholder="26.2389, 73.0243  or  https://maps.app.goo.gl/…"
                onChange={(e) => { setPasted(e.target.value); setPasteNote(null); }}
                onPaste={(e) => { const text = e.clipboardData.getData('text'); if (text.trim()) { e.preventDefault(); setPasted(text.trim()); applyPasted(text); } }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPasted(pasted); } }} />
              <Button size="dense" disabled={!pasted.trim() || open.isPending} onClick={() => applyPasted(pasted)}>{open.isPending ? 'Opening…' : 'Go'}</Button>
            </span></label>
          {pasteNote ? <p role={pasteNote.tone === 'bad' ? 'alert' : 'status'} className={cx('text-small', pasteNote.tone === 'bad' ? 'text-refused' : 'text-ink-muted')}>{pasteNote.text}</p> : null}
        </div>
      </div>
    </div>
  );
}

function StepRoute({ d, set }: StepProps) {
  const pinned = d.lat !== null && d.lng !== null;
  const s = useQuery({ queryKey: ['suggest-route', d.lat, d.lng], enabled: pinned,
    queryFn: () => rpc<Suggestion[]>('suggest_route_for_pin', { p_lat: d.lat, p_lng: d.lng, p_limit: 3 }) });
  const routes = useQuery({ queryKey: ['routes-for-onboarding'], queryFn: async () => {
    const { data, error } = await supabase.from('routes').select('id,code,name').eq('status', 'ACTIVE').order('code');
    if (error) throw error; return data as { id: string; code: string; name: string }[];
  } });
  return (
    <div className="grid gap-3">
      {!pinned ? <p className="text-small text-queued">Pin the door first (step 2); the route is suggested from it. Or choose one below.</p> : null}
      {s.data?.length ? (
        <ul className="grid gap-2">
          {s.data.map((r) => (
            <li key={r.routeId}>
              <button type="button" onClick={() => { set('routeId', r.routeId); set('position', r.position); }}
                className={cx('w-full rounded-md border px-3 py-2 text-left', d.routeId === r.routeId ? 'border-brand bg-brand/5' : 'border-line hover:bg-surface-sunken')}>
                <div className="font-medium">{r.route} — {r.name} <span className="text-small text-ink-muted">· {f.num(r.metres)} m away · rider {r.rider ?? <span className="text-queued">not set</span>}</span></div>
                <div className="text-small text-ink-muted">Stop {r.position} of {r.doors + 1}: after {r.before ? `${r.before.name} (${r.before.code})` : 'the start'}{r.after ? `, before ${r.after.name} (${r.after.code})` : ''}</div>
              </button>
            </li>))}
        </ul>) : null}
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Route"><Select value={d.routeId} onChange={(e) => { set('routeId', e.target.value); set('position', null); }} placeholder="Choose a route"
          options={(routes.data ?? []).map((r) => ({ value: r.id, label: `${r.code} — ${r.name}` }))} /></Field>
        <Field label="Stop number" hint="Leave empty to order by the pin"><Input type="number" min={1} value={d.position ?? ''} onChange={(e) => set('position', e.target.value ? Number(e.target.value) : null)} /></Field>
      </div>
    </div>
  );
}

/** An item's days: its own, else the plan's "How often". */
const itemDays = (d: Draft, it: Draft['items'][number]) => ({ every: it.every ?? d.pattern, weekdays: (it.weekdays ?? d.weekdays).map(Number) });

/** The plan sent to onboard_customer: as before when every item has the same days; otherwise a CYCLE plan with each
 *  item's own cycle from the first day (sales.plan_lines takes it as it is). */
function planOf(d: Draft, start: string) {
  const items = d.items.filter((i) => i.variantId);
  const days = items.map((i) => itemDays(d, i));
  const same = days.every((x) => x.every === days[0]!.every && x.weekdays.join() === days[0]!.weekdays.join());
  if (same || !start) {
    const first = days[0] ?? { every: d.pattern, weekdays: d.weekdays.map(Number) };
    return { pattern: first.every, weekdays: first.weekdays, items: items.map((i) => ({ variantId: i.variantId, quantity: i.quantity })) };
  }
  return { pattern: 'CYCLE', weekdays: [], items: items.map((i, k) => ({ variantId: i.variantId, quantity: i.quantity, cycle: cycleFor(days[k]!.every, i.quantity, days[k]!.weekdays, start) })) };
}

function StepPlan({ d, set, earliest }: StepProps & { earliest?: { date: string; words: string } }) {
  const variants = useQuery({ queryKey: ['variants-for-onboarding'], queryFn: async () => {
    const { data, error } = await supabase.from('product_variants').select('id,code,name,size_class').eq('status', 'ACTIVE').order('code');
    if (error) throw error; return data as Variant[];
  } });
  const setItem = (i: number, patch: Partial<Draft['items'][number]>) => set('items', d.items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="grid gap-3">
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="How often" hint="For every item; an item can have its own days below"><Select value={d.pattern} onChange={(e) => set('pattern', e.target.value as Draft['pattern'])}
          options={[{ value: 'DAILY', label: 'Every day' }, { value: 'ALTERNATE', label: 'Alternate days' }, { value: 'WEEKDAYS', label: 'Chosen weekdays' }]} /></Field>
        <Field label="First delivery" hint={earliest?.words}><Input type="date" min={earliest?.date} value={d.startDate || earliest?.date || ''} onChange={(e) => set('startDate', e.target.value)} /></Field>
        <Field label="Bottles on deposit" hint="The deposit amount is set by the owner"><Input type="number" min={0} max={50} value={d.depositBottles} onChange={(e) => set('depositBottles', e.target.value)} /></Field>
      </div>
      {d.pattern === 'WEEKDAYS' ? (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Weekdays">
          {WEEKDAYS.map(([v, w]) => (
            <Checkbox key={v} checked={d.weekdays.includes(v)} label={w}
              onChange={(e) => set('weekdays', e.target.checked ? [...d.weekdays, v] : d.weekdays.filter((x) => x !== v))} />))}
        </div>) : null}
      <div className="grid gap-2">
        {d.items.map((it, i) => {
          const own = itemDays(d, it);
          return (
            <div key={i} className="grid gap-1">
              <div className="grid grid-cols-[minmax(0,1fr)_7rem_10rem_auto] items-end gap-2">
                <Field label={i === 0 ? 'Item' : ''}><Select value={it.variantId} onChange={(e) => setItem(i, { variantId: e.target.value })} placeholder="Choose an item"
                  options={variantOptions(variants.data ?? [])} /></Field>
                <Field label={i === 0 ? 'Each time' : ''}><Input type="number" min={1} max={99} value={it.quantity} onChange={(e) => setItem(i, { quantity: Number(e.target.value) })} /></Field>
                <Field label={i === 0 ? 'Days' : ''}><Select aria-label="Days for this item" value={own.every}
                  onChange={(e) => setItem(i, { every: e.target.value as Every, weekdays: it.weekdays ?? d.weekdays })} options={EVERY_OPTIONS} /></Field>
                <Button size="dense" disabled={d.items.length === 1} onClick={() => set('items', d.items.filter((_, j) => j !== i))}>Remove</Button>
              </div>
              {own.every === 'WEEKDAYS' ? (
                <div className="flex flex-wrap gap-2" role="group" aria-label={`Weekdays for item ${i + 1}`}>
                  {WEEKDAYS.map(([v, w]) => {
                    const cur = it.weekdays ?? d.weekdays;
                    return <Checkbox key={v} checked={cur.includes(v)} label={w}
                      onChange={(e) => setItem(i, { every: 'WEEKDAYS', weekdays: e.target.checked ? [...cur, v] : cur.filter((x) => x !== v) })} />;
                  })}
                </div>) : null}
            </div>);
        })}
        <div><Button size="dense" onClick={() => set('items', [...d.items, { variantId: '', quantity: 1 }])}><Plus size={14} aria-hidden /> Another item</Button></div>
      </div>
      <Checkbox checked={d.trial} onChange={(e) => set('trial', e.target.checked)} label="A trial" description="Three days, then it ends by itself unless it is made regular." />
    </div>
  );
}

function StepLabel({ d, set }: StepProps) {
  return (
    <div className="grid gap-3">
      <div className="grid gap-2" role="radiogroup" aria-label="Door sticker">
        {([['allot', 'Print the customer\'s own sticker', 'The sticker carries the customer\'s code; print it from the customer\'s page and stick it on the gate.'],
           ['scan', 'Scan a sticker already on the door', 'Type or scan the code printed on it.'],
           ['later', 'Later', 'The customer waits in Starting soon until a sticker is on the door.']] as const).map(([v, label, note]) => (
          <label key={v} className={cx('flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2', d.labelMode === v ? 'border-brand bg-brand/5' : 'border-line')}>
            <input type="radio" name="label" className="mt-1" checked={d.labelMode === v} onChange={() => set('labelMode', v)} />
            <span><span className="font-medium">{label}</span><span className="block text-small text-ink-muted">{note}</span></span>
          </label>))}
      </div>
      {d.labelMode === 'scan' ? <Field label="Sticker code"><Input value={d.labelPayload} onChange={(e) => set('labelPayload', e.target.value.toUpperCase())} placeholder="DSG-JDH-00000-0" /></Field> : null}
      {d.labelMode === 'allot' ? <p className="flex items-center gap-1 text-small text-ink-muted"><Printer size={14} aria-hidden /> The code is given when the customer is saved.</p> : null}
    </div>
  );
}

function StepReview({ d, start }: { d: Draft; start: string }) {
  const checks = useMemo(() => [
    { ok: d.lat !== null, text: d.lat !== null ? 'The door is pinned' : 'The door has no pin yet (the office adds it later; the delivery is made at the door)' },
    { ok: !!d.routeId, text: d.routeId ? `On a route${d.position ? `, stop ${d.position}` : ''}` : 'Not on a route yet (step 3)' },
    { ok: d.items.some((i) => i.variantId), text: 'A plan with at least one item' },
    { ok: d.labelMode !== 'later', text: d.labelMode === 'later' ? 'No door sticker yet (it follows)' : 'A door sticker' },
    ...(d.moreDoors.length ? [{ ok: true, text: `${d.moreDoors.length} more address(es) kept on record` }] : []),
  ], [d]);
  return (
    <div className="grid gap-3">
      <p className="text-body">
        <span className="font-medium">{d.name || 'The customer'}</span>, first delivery <span className="font-medium">{start ? f.date(start) : '—'}</span>
        {d.trial ? ' (a 3-day trial)' : ''}. The usual rider, the price of each item and the start date are checked when you press Activate.
      </p>
      <ul className="grid gap-1">
        {checks.map((c) => (
          <li key={c.text} className={cx('text-small', c.ok ? 'text-accepted' : 'text-queued')}>{c.ok ? '✓' : '•'} {c.text}</li>))}
      </ul>
      <p className="text-small text-ink-muted">If anything still blocks, the customer is saved and waits in Starting soon, with what is missing.</p>
    </div>
  );
}
